<template>

  <!-- XOL-67 (v2.2 layout, decision 176; refined by 183 and 186): the fields above the map are TWO TITLED CARDS, in the create form (mode 'create') and in Step 1 (mode 'step'
       with `sections`): the LEFT, "Details", is the parent's `lead` slot (create: Client, Site Name, Site ID; Step 1: the facts, Site Name, Site ID), the Locality and
       Lat / Lng; the RIGHT, "Structure Details", is the Station Type and the structure fields. Then "Find a place" and the map. A 'step' without `sections` keeps the
       single flow (the Locality, the Station Type, the requirements, the search row and Lat / Lng above the map; nothing passes it any more). Below 768px (`stacked`) it is one column in the same order.
       XOL-67 (V2, v2.1; decisions 153, 157, 158 Q2; v2.2: decisions 168 to 174): the fields of an RTA's REQUEST, shared by the create form (AcquireRequest, mode 'create')
       and Step 1 (RtaRequest, V3, mode 'step'). Top to bottom (v2.2 UX spec C1): the locality (optional), the Station Type and the requirements, then the
       POINT block LAST: the "Find a place" search, Lat / Lng, the map with the blue crosshairs and its pin button, and the hint. (The two names sit above this
       component, in the form that owns them.) It holds nothing of its own: `value` is the request as ONE flat object keyed by the wire names of `state.nominal`,
       a change goes out as `input` (the whole new object, for v-model) and as `change` (only the keys this edit changed: one key for an input, latitude +
       longitude TOGETHER for the map and its button, so a save sends the pair as one body, P-S27). The parent owns the saving (Save at create, the auto-save
       on Step 1). It renders its own el-form (label-position top, inline): put it NEXT TO other forms, never inside one (inline-form CSS leaks into a nested form). -->
  <div class="rta-request-fields" :class="{ 'is-stacked': stacked, 'is-columns': columns }">
  <el-form label-position="top" inline @submit.native.prevent>

    <!-- v2.2 layout (decisions 176, 183, 186; create and Step 1 with `sections`): TWO COLUMNS above the map. Left: whatever the parent puts in the `lead` slot, then the
         Locality and Lat / Lng. Right: the Station Type and the structure fields. With `columns` off (a 'step' without `sections`) the two wrappers are plain blocks and the
         fields flow as they did before decision 186. -->
    <div :class="{ 'rta-columns': columns }">

      <div :class="{ 'rta-column': columns }" data-column="left">
      <!-- Decisions 183, 186: each column is a titled card, as the option's Land Details / Structure Details are (the create form and Step 1 with `sections`). -->
      <component :is="columns ? 'el-card' : 'div'" :shadow="columns ? 'never' : null" :class="{ 'rta-section': columns }" data-section="details">
        <template v-if="columns" #header><div class="clearfix"><span class="text-primary"><i class="fa fa-info-circle" />&nbsp;Details</span></div></template>

        <slot name="lead"></slot>

        <!-- The locality (optional, decision 168) and, with `columns` off only, the Station Type, in `leftFields` order. Keys are on the children (a `template v-for` takes none). -->
        <template v-for="field in leftFields">

          <template v-if="field === 'state'">
            <!-- A stored path that stops above a leaf shows nothing in the cascader (Element shows leaf paths only), which would read as "no locality": the stored
                 names are read out, in bold, above it. -->
            <el-form-item v-if="can('state') && localityShortOfLeaf" key="state-readout" class="rta-locality-readout" label="Locality" data-field="state-readout">
              <b>{{ localityText }}</b>
            </el-form-item>
            <select-locality
              v-if="can('state')"
              key="state"
              data-field="state"
              class="rta-locality"
              :label="localityShortOfLeaf ? '' : 'Locality'"
              clearable
              :value="localityValue"
              @change="onLocality" />
            <el-form-item v-else key="state-readonly" label="Locality" data-field="state">
              <b>{{ localityText }}</b>
            </el-form-item>

            <p v-if="localityError" key="state-error" class="rta-field-error" role="alert">{{ localityError }}</p>
          </template>

          <template v-else>
            <select-coverage
              v-if="can('stationType')"
              key="stationType"
              data-field="stationType"
              label="Station Type"
              :value="value.stationType"
              @change="onField('stationType', $event)" />
            <el-form-item v-else key="stationType-readonly" label="Station Type" data-field="stationType">
              <b>{{ shown(value.stationType) }}</b>
            </el-form-item>
          </template>

        </template>

        <!-- Decisions 183, 186: Lat / Lng sit in Details, under the Locality, in the create form and in Step 1 (with `columns` off they stay above the map, in its `above-map` slot). -->
        <div v-if="columns" class="rta-point" data-point="details">
          <div class="rta-point-row">
            <float-input
              v-if="can('latitude')"
              class="rta-lat"
              data-field="latitude"
              label=""
              aria-label="Latitude"
              :decimals="12"
              :value="value.latitude"
              placeholder="Latitude"
              prefix="Lat"
              suffix="&deg;"
              @change="onCoordinate('latitude', $event)"
              />
            <span v-else class="rta-fact" data-field="latitude">Latitude:&nbsp;<b>{{ shown(value.latitude) }}</b><span v-if="hasValue(value.latitude)">&deg;</span></span>

            <float-input
              v-if="can('longitude')"
              class="rta-lng"
              data-field="longitude"
              label=""
              aria-label="Longitude"
              :decimals="12"
              :value="value.longitude"
              placeholder="Longitude"
              prefix="Lng"
              suffix="&deg;"
              @change="onCoordinate('longitude', $event)"
              />
            <span v-else class="rta-fact" data-field="longitude">Longitude:&nbsp;<b>{{ shown(value.longitude) }}</b><span v-if="hasValue(value.longitude)">&deg;</span></span>
          </div>

          <p v-if="pointError" class="rta-field-error" role="alert">{{ pointError }}</p>
        </div>

      </component>
      </div>

      <div :class="{ 'rta-column': columns }" data-column="right">
      <component :is="columns ? 'el-card' : 'div'" :shadow="columns ? 'never' : null" :class="{ 'rta-section': columns }" data-section="structure">
        <template v-if="columns" #header><div class="clearfix"><span class="text-info"><i class="fa fa-broadcast-tower" />&nbsp;Structure Details</span></div></template>

        <!-- Decisions 183, 186: the Station Type heads Structure Details, as on the option, in the create form and in Step 1. (With `columns` off it stays in the left flow, `leftFields`.) -->
        <template v-if="columns">
          <select-coverage
            v-if="can('stationType')"
            key="stationType-structure"
            data-field="stationType"
            label="Station Type"
            :value="value.stationType"
            @change="onField('stationType', $event)" />
          <el-form-item v-else key="stationType-structure-readonly" label="Station Type" data-field="stationType">
            <b>{{ shown(value.stationType) }}</b>
          </el-form-item>
        </template>

        <!-- The requirements: all optional. -->
        <div class="rta-requirements">
          <select-structure
            v-if="can('structure_id')"
            data-field="structure_id"
            label="Structure Type"
            :value="value.structure_id"
            @change="onField('structure_id', $event)" />
          <el-form-item v-else label="Structure Type" data-field="structure_id">
            <b>{{ structureLabel || shown(value.structure_id) }}</b>
          </el-form-item>

          <integer-input
            v-if="can('structureHeight')"
            data-field="structureHeight"
            label="Structure Height"
            :min="1"
            :max="121"
            suffix="m."
            :value="value.structureHeight"
            @change="onWhole('structureHeight', $event)" />
          <el-form-item v-else label="Structure Height" data-field="structureHeight">
            <b>{{ metres(value.structureHeight) }}</b>
          </el-form-item>

          <integer-input
            v-if="can('antennaHeight')"
            data-field="antennaHeight"
            label="Antenna AGL"
            :min="0"
            :max="121"
            suffix="m."
            placeholder="Height above ground in metres"
            empty-is-null
            :value="value.antennaHeight"
            @change="onAntenna($event)" />
          <el-form-item v-else label="Antenna AGL" data-field="antennaHeight">
            <b>{{ metres(value.antennaHeight) }}</b>
          </el-form-item>

          <integer-input
            v-if="can('width')"
            data-field="width"
            label="Site Width"
            suffix="m."
            placeholder="width in metres"
            :value="value.width"
            @change="onWhole('width', $event)" />
          <el-form-item v-else label="Site Width" data-field="width">
            <b>{{ metres(value.width) }}</b>
          </el-form-item>

          <integer-input
            v-if="can('depth')"
            data-field="depth"
            label="Site Depth"
            suffix="m."
            placeholder="depth in metres"
            :value="value.depth"
            @change="onWhole('depth', $event)" />
          <el-form-item v-else label="Site Depth" data-field="depth">
            <b>{{ metres(value.depth) }}</b>
          </el-form-item>
        </div>

        <p v-if="requirementsError" class="rta-field-error" role="alert">{{ requirementsError }}</p>

      </component>
      </div>

    </div>

    <!-- 3. The requested point, last (H-2). Decisions 183, 186 (create and Step 1): Lat / Lng are in Details, and "Find a place" sits just above the map (the atom's
         own search row; `search-beside` is off). With `columns` off: the search row, then Lat / Lng in the map's `above-map` slot, then the map. Nothing places the pin by a click (decision 170): the pin button, a drag of the crosshairs, or typing Lat / Lng. The button is a Google
         control on the map (so it stays in fullscreen), described by the hint below. -->
    <google-map
      ref="map"
      class="rta-map"
      :lat="mapStart.lat"
      :lng="mapStart.lng"
      :zoom="mapStart.zoom"
      :pinned="pinMarkers ? false : hasPoint"
      :draggable="pointEditable"
      show-nominal
      pin-title="Requested point"
      :pin-markers="pinMarkers"
      width="100%"
      aspect="16:10"
      :searchable="pointEditable"
      :search-beside="false"
      :centre-button="pinButton"
      :centre-title="pinButtonTitle"
      :centre-described-by="mapHint.length ? hintId : null"
      @update="onMap"
      @pin-to-centre="onCentre"
      @pin-click="onPin">

      <template v-if="!columns" #above-map>
        <div class="rta-point">
        <div class="rta-point-row">

          <float-input
            v-if="can('latitude')"
            class="rta-lat"
            data-field="latitude"
            label=""
            aria-label="Latitude"
            :decimals="12"
            :value="value.latitude"
            placeholder="Latitude"
            prefix="Lat"
            suffix="&deg;"
            @change="onCoordinate('latitude', $event)"
            />
          <span v-else class="rta-fact" data-field="latitude">Latitude:&nbsp;<b>{{ shown(value.latitude) }}</b><span v-if="hasValue(value.latitude)">&deg;</span></span>

          <float-input
            v-if="can('longitude')"
            class="rta-lng"
            data-field="longitude"
            label=""
            aria-label="Longitude"
            :decimals="12"
            :value="value.longitude"
            placeholder="Longitude"
            prefix="Lng"
            suffix="&deg;"
            @change="onCoordinate('longitude', $event)"
            />
          <span v-else class="rta-fact" data-field="longitude">Longitude:&nbsp;<b>{{ shown(value.longitude) }}</b><span v-if="hasValue(value.longitude)">&deg;</span></span>

        </div>

        <p v-if="pointError" class="rta-field-error" role="alert">{{ pointError }}</p>
        </div>
      </template>

    </google-map>

    <p v-if="mapHint.length" :id="hintId" class="rta-map-hint text-muted"><small><template v-for="(part, i) in mapHint"><b v-if="part.bold" :key="i">{{ part.text }}</b><template v-else>{{ part.text }}</template></template></small></p>
    <slot name="map-hint"></slot>

    <!-- Said to a screen reader when the pin button is pressed: the press adds or moves the pin, which a sighted user sees. -->
    <span class="rta-sr-only" aria-live="polite">{{ pinAnnouncement }}</span>

  </el-form>
  </div>

</template>

<script>

import { coordinate } from '@/lib/rtaNominal'
import { mapCentre } from '@/lib/rtaOption'
import { requestProblems, localityKeys, localityPath, localityShort } from '@/lib/rtaRequestForm'
import { localityData } from '@/components/Data'

import SelectCoverage from '@/components/atoms/selects/SelectCoverage'
import SelectLocality from '@/components/atoms/selects/SelectLocality'
import SelectStructure from '@/components/atoms/selects/SelectStructure'

import GoogleMap from '@/components/atoms/GoogleMap'

// The point is stored to the 7th decimal (about a centimetre): the map hands over a long float and the field would show all of it.
function round7 (n) {
  return Math.round(Number(n) * 1e7) / 1e7
}

function present (v) {
  return v !== undefined && v !== null && String(v).trim() !== ''
}

// The fields of one block, for its single error line.
var POINT = ['latitude', 'longitude']
var LOCALITY = ['region', 'state', 'district', 'subdistrict', 'zone']
var REQUIREMENTS = ['stationType', 'structure_id', 'structureHeight', 'antennaHeight', 'width', 'depth']

// Decision 184: the zoom typed coordinates are shown at, building level (as a new extra marker, decision 180); a map already closer is left as it is.
var POINT_ZOOM = 19

export default {
  name: 'rta-request-fields',

  components: {
    GoogleMap,
    SelectCoverage,
    SelectLocality,
    SelectStructure
  },

  props: {
    // Decision 186: Step 1 (mode 'step') laid out as the create form: Details and Structure Details, the parent's `lead` slot at the top of Details.
    sections: {
      type: Boolean,
      default: false
    },


    // The request, flat, by the wire names of `state.nominal` (latitude, longitude, region, state, district, subdistrict, zone, stationType, structure_id,
    // structureHeight, antennaHeight, width, depth). Never changed here: a change goes out as `input` and `change`.
    value: {
      type: Object,
      required: true
    },

    // Which fields are inputs: true (all), false (none, bold values), or an object { field: true } by wire name, from rtaStage.canEditRequestField. A field
    // the object leaves out is read-only. The locality is `state`, and its five levels move together.
    editable: {
      type: [Boolean, Object],
      default: true
    },

    // Messages to show, { field: message }, by wire name: the server's 422 mapped by rtaNominal.nominalFieldErrors, or the parent's own check. They win over
    // the form's own check of a field the user has changed.
    errors: {
      type: Object,
      default: function () { return {} }
    },

    // 'create' (the Popup: a hint to place the point) or 'step' (Step 1: the auto-save, "Each change saves on its own").
    mode: {
      type: String,
      default: 'create'
    },

    // The Structure Type in words, for the read-only value (the id alone says nothing). The select shows its own.
    structureLabel: {
      type: String,
      default: ''
    },

    // Step 1 (V3): the map's pins from rtaOption.markersForRequestMap: the crosshairs (draggable for who may edit the request) and every option's
    // pin, read-only; a click on an option's pin is `open-option` (the option's id). null (create): the crosshairs are the map's centre pin, as before.
    // The crosshairs are in this list, so the parent builds it from the values the form holds: a typed Lat / Lng moves them.
    pinMarkers: {
      type: Array,
      default: null
    },

    // A phone or small tablet (the app's `isMediumOrLess`, UX spec 7): every field stacks, full width. The parent says it, so the block and its parent agree.
    stacked: {
      type: Boolean,
      default: false
    }

  },

  data () {
    return {
      // The fields the user has changed: only they show the form's own check (a field never touched stays quiet until the parent says so).
      touched: [],

      // What a screen reader hears when the pin button adds or moves the pin (UX spec C4).
      pinAnnouncement: '',

      // The zoom the user had the map at when they pressed the pin button: the map stays there once the pin is on it (mapStart would otherwise zoom it to 14,
      // out of a street a search just showed). null until a press.
      viewZoom: null
    }
  },

  computed: {

    // The two titled sections (decisions 176, 183): the create form, and Step 1 (mode 'step' with `sections`, decision 186). Without `sections` a 'step' keeps the single flow.
    columns () {
      return this.mode === 'create' || this.sections
    },

    // The left column's own fields, top to bottom (`state` is the Locality). Create: the Locality only (the Station Type heads Structure Details). Single flow: the Locality, then the Station Type.
    leftFields () {
      return this.columns ? ['state'] : ['state', 'stationType']
    },

    hasPoint () {
      return coordinate(this.value.latitude) !== null && coordinate(this.value.longitude) !== null
    },

    // Can the point be moved? Both coordinates (they always agree).
    pointEditable () {
      return this.can('latitude') && this.can('longitude')
    },

    // The map starts on the point; with none, on the chosen state or region (else the middle of Malaysia): a centre only, no pin until the user places one.
    mapStart () {
      if (this.hasPoint) {
        return { lat: Number(this.value.latitude), lng: Number(this.value.longitude), zoom: this.viewZoom || 14 }
      }

      return mapCentre(localityPath(this.value))
    },

    // The cascader's path: the levels the request holds, with no empty slot (the cascader takes a path).
    localityValue () {
      return localityPath(this.value).filter(function (l) { return l !== null })
    },

    // The stored locality stops above a leaf: the cascader would show nothing for it.
    localityShortOfLeaf () {
      return localityShort(this.localityValue, localityData.regions)
    },

    localityText () {
      var path = this.localityValue

      return path.length ? path.join(' / ') : 'Not entered'
    },

    // The form's own check of the fields the user has changed, then the parent's messages over it.
    problems () {
      var own = requestProblems(this.value, this.touched)

      return Object.assign({}, own, this.errors)
    },

    pointError () {
      return this.joined(POINT)
    },

    localityError () {
      return this.joined(LOCALITY)
    },

    requirementsError () {
      return this.joined(REQUIREMENTS)
    },

    // Are there options' pins on the map (Step 1 only)?
    hasOptionPins () {
      return (this.pinMarkers || []).some(function (m) { return !!m && m.kind === 'pin' })
    },

    // The pin button's visible label (UX spec C4): Add while there is no pin, Center once there is one; the short form on a phone or small tablet.
    pinButton () {
      if (!this.pointEditable) {
        return null
      }

      if (this.stacked) {
        return this.hasPoint ? 'Center pin' : 'Add pin'
      }

      return this.hasPoint ? 'Center requested point pin' : 'Add requested point pin'
    },

    // Its native hover text (no el-tooltip: its popper is hidden in fullscreen).
    pinButtonTitle () {
      return this.hasPoint
        ? 'Moves the requested point to the center of the map. Then drag it into place.'
        : 'Puts the requested point at the center of the map. Then drag it into place.'
    },

    hintId () {
      return 'rta-map-hint-' + this._uid
    },

    // The hint under the map, as [{ text, bold? }]: the pin button's label is bold in it (UX spec "Copy: map hints"). Editable on Step 1: how to move the
    // point ("Each change saves on its own"), then (options exist) that their pins are read-only and open on a click; read-only: only that second sentence.
    mapHint () {
      var pins = this.hasOptionPins ? 'The options\' pins are shown read-only: click one to open it.' : ''
      var more = pins ? ' ' + pins : ''
      var label = this.pinButton

      function t (text) { return { text: text } }
      function b (text) { return { text: text, bold: true } }

      if (!this.pointEditable) {
        return pins ? [t(pins)] : []
      }

      if (this.mode === 'step') {
        return this.hasPoint
          ? [t('Drag the crosshairs, press '), b(label), t(', or type Lat / Lng to move the requested point. Each change saves on its own.' + more)]
          : [t('Find a place or move the map, then press '), b(label), t(' and drag the pin into place, or type Lat / Lng. Each change saves on its own.' + more)]
      }

      return this.hasPoint
        ? [t('Drag the crosshairs, or press '), b(label), t(' to bring them to the center of the map. Lat / Lng follow.')]
        : [t('Find a place or move the map, then press '), b(label), t(' to put the pin at the center, and drag it into place. You can also type Lat / Lng, or place it later on Step 1.')]
    }

  },

  methods: {

    // May this field be edited? (editable is true, false, or an object by wire name; the locality is the field `state`: its five levels move together.)
    can (field) {
      if (typeof this.editable === 'object' && this.editable !== null) {
        return this.editable[field] === true
      }

      return this.editable !== false
    },

    hasValue (v) {
      return present(v)
    },

    // A read-only value in words: "Not entered" for none.
    shown (v) {
      return present(v) ? String(v) : 'Not entered'
    },

    metres (v) {
      return present(v) ? v + ' m.' : 'Not entered'
    },

    // The distinct messages of a block's fields, one line.
    joined (fields) {
      var self = this
      var out = []

      fields.forEach(function (f) {
        var m = self.problems[f]

        if (m && out.indexOf(m) === -1) {
          out.push(m)
        }
      })

      return out.join(' ')
    },

    // The one place a value changes: tell the parent the new request (`input`) and the keys that changed (`change`).
    set (changes) {
      var self = this

      Object.keys(changes).forEach(function (k) {
        if (self.touched.indexOf(k) === -1) {
          self.touched.push(k)
        }
      })

      this.$emit('input', Object.assign({}, this.value, changes))
      this.$emit('change', changes)
    },

    onField (field, v) {
      var changes = {}

      changes[field] = present(v) ? v : null
      this.set(changes)
    },

    // A coordinate: a typed 0 or a cleared field is "no value" (null), as everywhere (P-S22).
    onCoordinate (field, v) {
      var n = coordinate(v)
      var changes = {}

      changes[field] = n
      this.set(changes)

      // Decision 184 (Carlos, 2026-10-29): typed Lat and Lng place the requested point's pin at once (the pin follows the value) and the map goes there,
      // zoomed in to building level, never out. A field reports on Enter, Tab or blur, not per keystroke (Step 1 saves each change).
      var lat = field === 'latitude' ? n : coordinate(this.value.latitude)
      var lng = field === 'longitude' ? n : coordinate(this.value.longitude)
      var map = this.$refs.map

      if (lat !== null && lng !== null && map && typeof map.focusOn === 'function') {
        map.focusOn({ lat: lat, lng: lng }, POINT_ZOOM)
      }
    },

    // A whole number of metres. A cleared field arrives as 0 (the integer input rounds ''), and 0 m is no height, width or depth: it is EMPTY (null), never
    // a 0 that would be saved or sent.
    onWhole (field, v) {
      var n = Number(v)
      var changes = {}

      changes[field] = isNaN(n) || n === 0 ? null : n
      this.set(changes)
    },

    // Decision 185: Antenna AGL keeps a 0 (it is a value, 0 to 121); only a cleared field (null from the input's `empty-is-null`) is empty.
    onAntenna (v) {
      var n = v === null || v === undefined || v === '' ? null : Number(v)

      this.set({ antennaHeight: n === null || isNaN(n) ? null : n })
    },

    // The cascader's full path from the region: the five levels move together, a level it did not reach as null.
    onLocality (path) {
      this.set(localityKeys(path))
    },

    // A click on one of Step 1's pins: an option's opens that option (the crosshairs open nothing).
    onPin (m) {
      if (m && m.opensOption) {
        this.$emit('open-option', m.opensOption)
      }
    },

    // A drop of the crosshairs: latitude and longitude change TOGETHER (one `change`, so one body on Step 1).
    onMap (pos) {
      if (!pos || !this.pointEditable) {
        return
      }

      this.set({ latitude: round7(pos.lat), longitude: round7(pos.lng) })
    },

    // The pin button (UX spec C4): the pin is added at the centre of the map, or moved there, exactly as a drop. A press says what it did to a screen reader.
    onCentre (pos) {
      if (!pos || !this.pointEditable) {
        return
      }

      var said = this.hasPoint ? 'Requested point moved to the center of the map.' : 'Requested point added at the center of the map.'
      var self = this

      if (typeof pos.zoom === 'number' && pos.zoom > 0) {
        this.viewZoom = pos.zoom
      }

      this.onMap(pos)

      // The same sentence twice in a row is only announced again if the region changed in between.
      this.pinAnnouncement = ''
      this.$nextTick(function () { self.pinAnnouncement = said })
    }

  }
}
</script>

<style scoped>
.rta-point-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
}

.rta-lat {
  width: 200px;
  margin-right: 10px;
}

.rta-lng {
  width: 220px;
}

.rta-fact {
  margin-right: 24px;
}

.rta-map {
  margin: 4px 0 0;
}

/* The hidden text of a live region (this repo has no global .sr-only). */
.rta-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.rta-map-hint {
  margin: 4px 0 12px;
}

/* Carlos 2026-10-28: the map hint at 12px, the size of the create form's Site Name hint (the <small> alone rendered larger). */
.rta-map-hint small {
  font-size: 12px;
}

.rta-locality {
  width: 300px;
  display: block;
}

/* The read-out of a locality the cascader cannot show: a line of its own above the cascader. */
.rta-request-fields .rta-locality-readout {
  display: block;
  margin-bottom: 4px;
}

/* A message under the block of fields it is about (the numeric inputs have no error line of their own). */
.rta-field-error {
  color: #F56C6C;
  font-size: 12px;
  line-height: 1.4;
  margin: 2px 0 8px;
}

/* Decision 176 (create): two columns above the map, each field the width of its column up to 420px (the width of the place search). */
.rta-columns {
  display: flex;
  align-items: flex-start;
  column-gap: 40px;
}

.rta-column {
  flex: 1 1 0;
  min-width: 0;
}

.rta-column >>> .el-form-item {
  display: block;
  margin-right: 0;
}

.rta-column >>> .el-form-item__content,
.rta-column >>> .el-select,
.rta-column >>> .el-cascader,
.rta-column >>> .el-input {
  width: 100%;
  max-width: 420px;
}

.is-columns .rta-locality {
  width: 100%;
  max-width: 420px;
}

/* Decision 183: the create form's two titled cards, the option drawer's look; the map follows below them. */
.rta-section {
  height: 100%;
}

.is-columns .rta-columns {
  align-items: stretch;
  margin-bottom: 16px;
}

.is-columns .rta-point-row {
  flex-wrap: nowrap;
  column-gap: 12px;
  max-width: 420px;
}

.is-columns .rta-point-row .rta-lat,
.is-columns .rta-point-row .rta-lng {
  flex: 1 1 0;
  min-width: 0;
  margin-right: 0;
}

/* A phone or small tablet (UX spec 7, M-32): one column, every field full width. */
.is-stacked .rta-columns {
  display: block;
}

.is-stacked .rta-lat,
.is-stacked .rta-lng,
.is-stacked .rta-locality {
  width: 100%;
  margin-right: 0;
}

.is-stacked >>> .el-form-item {
  display: block;
  margin-right: 0;
}

.is-stacked >>> .el-select,
.is-stacked >>> .el-cascader {
  width: 100%;
}
</style>
